import { httpClient } from '../httpClient';
import {
  IEpisode,
  CreateEpisodeRequest,
  UpdateEpisodeRequest,
  PaginatedResponse,
  EpisodeQuery,
  PaginationQuery,
  IVideoClip,
  BatchGenerateResourcesRequest
} from '@packages/global';

/**
 * 用户Episode API 模块
 * 处理用户集管理操作
 */
export class EpisodeApi {
  /**
   * 获取用户集分页列表（POST，强类型）
   */
  async episodePageList(queryData: PaginationQuery<EpisodeQuery>): Promise<PaginatedResponse<IEpisode>> {
    return httpClient.post<PaginatedResponse<IEpisode>>('/user/episodes/page', queryData);
  }

  /**
   * 获取单个集详情
   */
  async getEpisode(id: string): Promise<IEpisode> {
    return httpClient.get<IEpisode>(`/user/episodes/${id}`);
  }

  /**
   * 获取剧集的所有集
   */
  async getProjectEpisodes(projectId: string): Promise<IEpisode[]> {
    return httpClient.get<IEpisode[]>(`/user/episodes/projects/${projectId}/episodes`);
  }

  /**
   * 获取集的所有视频片段
   */
  async getEpisodeVideoClips(episodeId: string): Promise<IVideoClip[]> {
    return httpClient.get<IVideoClip[]>(`/user/episodes/${episodeId}/videoclips`);
  }

  /**
   * 创建集
   */
  async createEpisode(data: CreateEpisodeRequest): Promise<IEpisode> {
    return httpClient.post<IEpisode>('/user/episodes', data);
  }

  /**
   * 更新集
   */
  async updateEpisode(id: string, data: UpdateEpisodeRequest): Promise<IEpisode> {
    return httpClient.put<IEpisode>(`/user/episodes/${id}`, data);
  }

  /**
   * 删除集
   */
  async deleteEpisode(id: string): Promise<void> {
    return httpClient.delete<void>(`/user/episodes/${id}`);
  }

  /**
   * 生成视频片段
   */
  async generateVideoClips(episodeId: string, data: {
    scriptId: string;
    text2imageModelId?: string;
    imageEditModelId?: string;
    text2speechModelId?: string;
    aspectRatio?: string;
    generateEndFrame?: boolean;
  }): Promise<{ videoClips: number }> {
    return httpClient.post<{ videoClips: number }>(`/user/episodes/${episodeId}/generate-videoclips`, data);
  }

  /**
   * 从剧本创建新集
   */
  async createEpisodeFromScript(scriptId: string, data?: {
    text2imageModelId?: string;
    imageEditModelId?: string;
    text2speechModelId?: string;
    aspectRatio?: string;
    generateEndFrame?: boolean;
  }): Promise<{ episode: IEpisode, videoClips: number }> {
    return httpClient.post<{ episode: IEpisode, videoClips: number }>(`/user/episodes/create-from-script/${scriptId}`, data || {});
  }

  /**
   * 重新生成分镜（拷贝当前分镜脚本并生成新分镜）
   */
  async regenerateStoryboard(episodeId: string, data: {
    scriptId: string;
    text2imageModelId?: string;
    imageEditModelId?: string;
    text2speechModelId?: string;
    aspectRatio?: string;
    generateEndFrame?: boolean;
  }): Promise<{ newEpisodeId: string; videoClips: number }> {
    return httpClient.post<{ newEpisodeId: string; videoClips: number }>(`/user/episodes/${episodeId}/regenerate-storyboard`, data);
  }

  /**
   * 批量生成视频提示词
   */
  async batchGenerateVideoPrompts(episodeId: string): Promise<{ jobId: string; status: string }> {
    return httpClient.post<{ jobId: string; status: string }>(`/user/episodes/${episodeId}/batch-generate-video-prompts`);
  }

  /**
   * 查询批量提示词生成状态
   */
  async getBatchPromptGenerationStatus(episodeId: string, jobId: string): Promise<{
    episodeId: string;
    jobId: string;
    status: string;
    progress: number;
    total: number;
    successCount: number;
    error?: string;
  }> {
    return httpClient.get<{
      episodeId: string;
      jobId: string;
      status: string;
      progress: number;
      total: number;
      successCount: number;
      error?: string;
    }>(`/user/episodes/${episodeId}/batch-prompt-generation-status/${jobId}`);
  }

  /**
   * 批量生成资源（图片+配音）
   */
  async batchGenerateResources(episodeId: string, data?: BatchGenerateResourcesRequest): Promise<{ jobId: string; episodeId: string; status: string }> {
    return httpClient.post<{ jobId: string; episodeId: string; status: string }>(`/user/episodes/${episodeId}/batch-generate-resources`, data || {});
  }

  /**
   * 查询批量生成状态
   */
  async getBatchGenerationStatus(episodeId: string): Promise<{
    episodeId: string;
    jobId?: string;
    status: string;
    progress?: number;
    result?: any;
    episodeStatus: string;
    message?: string;
  }> {
    return httpClient.get<{
      episodeId: string;
      jobId?: string;
      status: string;
      progress?: number;
      result?: any;
      episodeStatus: string;
      message?: string;
    }>(`/user/episodes/${episodeId}/batch-generation-status`);
  }
}

// 导出单例实例
export const episodeApi = new EpisodeApi();
